import { apiClient } from "utils/clients";
import externals from "utils/externals";
import { handleApiAction } from "utils/actions";
import { fetchCvoExistsUnderAgent, hasCvoProductContextForProvider } from "../../../utils/cvoMarketplaceUtils";

// Returns a promise resolving to whether a stored marketplace context for the standalone CVO product
// (serviceName === CVO_PRODUCT_SERVICE_NAME) exists for the given cloud provider on the current account.
const fetchHasCvoProductContext = (getState, cloudProvider) => {
    const baseUrl = externals.subscriptionServiceBackend;
    const accountId = getState().tenancy?.selectedAccountId;
    return apiClient.get(`${baseUrl}/subscriptions/marketplace-context`, { params: { accountId } })
        .then(({ data }) => hasCvoProductContextForProvider(data?.contexts, cloudProvider))
        .catch(e => {
            // Log only the message — the raw error/response may carry request headers (e.g. the
            // Authorization bearer token) that must not land in the browser console.
            console.error("[MARKETPLACE-CONTEXT] context existence check failed", e?.message);
            return false;
        });
};

// Whether a CVO (resourceClass VSA) exists under the given agent — delegates to the shared helper.
const fetchHasCvoUnderAgent = (getState, selectedAgentUserId) =>
    fetchCvoExistsUnderAgent({ selectedOrgId: getState().tenancy?.selectedOrgId, selectedAgentUserId });

export default ({ dispatch, getState }) => next => action => {
    const { type } = action;

    if (type === "MARKETPLACE-CONTEXT:FETCH") {
        const baseUrl = externals.subscriptionServiceBackend;
        const accountId = getState().tenancy?.selectedAccountId;

        const fetchPromise = apiClient.get(`${baseUrl}/subscriptions/marketplace-context`, { params: { accountId } });

        return handleApiAction(action, () => fetchPromise).catch(e => {
            // Background context load — log only the message (raw error may carry auth headers/tokens)
            // and do not surface a user-facing notification.
            console.error("[MARKETPLACE-CONTEXT:FETCH] failed", e?.message);
        });
    }

    if (type === "MARKETPLACE-CONTEXT:SAVE") {
        const baseUrl = externals.subscriptionServiceBackend;
        const { cloudProvider, serviceName, registrationToken } = action.payload || {};

        const savePromise = apiClient.post(`${baseUrl}/subscriptions/marketplace-context`, {
            cloudProvider,
            serviceName,
            registrationToken
        });

        return handleApiAction(action, () => savePromise)
            .then((result) => {
                if (result?.written === true) {
                    dispatch({ type: "MARKETPLACE-CONTEXT:FETCH" });
                }
            })
            .catch(e => {
                // Background context save — log only the message (raw error may echo tokens/auth headers)
                // and do not surface a user-facing notification.
                console.error("[MARKETPLACE-CONTEXT:SAVE] failed", e?.message);
            });
    }

    if (type === "MARKETPLACE-CONTEXT:CLEAR") {
        const baseUrl = externals.subscriptionServiceBackend;
        const cloudProvider = (action.payload?.cloudProvider || getState().marketplaceContext?.row?.cloudProvider || "").toLowerCase();

        if (!cloudProvider) {
            console.error("[MARKETPLACE-CONTEXT:CLEAR] missing cloudProvider; skipping delete");
            return next(action);
        }

        return handleApiAction(action, () =>
            apiClient.delete(`${baseUrl}/subscriptions/marketplace-context/${cloudProvider}`)
        ).catch(e => {
            console.error("[MARKETPLACE-CONTEXT:CLEAR] failed (handled silently)", e?.message);
        });
    }

    if (type === "MARKETPLACE-CONTEXT:CLEAR-IF-EXISTS") {
        const { cloudProvider } = action.payload || {};
        return fetchHasCvoProductContext(getState, cloudProvider).then(hasContext => {
            if (hasContext) {
                dispatch({ type: "MARKETPLACE-CONTEXT:CLEAR", payload: { cloudProvider } });
            }
        });
    }

    if (type === "MARKETPLACE-CONTEXT:CLEAR-IF-CVO-EXISTS") {
        const selectedAgentUserId = getState().tenancy?.selectedAgentUserId;
        return fetchHasCvoUnderAgent(getState, selectedAgentUserId).then(hasCvo => {
            if (hasCvo) {
                const cloudProvider = getState().marketplaceContext?.row?.cloudProvider;
                dispatch({ type: "MARKETPLACE-CONTEXT:CLEAR", payload: { cloudProvider } });
            }
        }).catch(e => {
            // Existence check couldn't be verified (network/API failure) — leave the context intact
            // rather than risk clearing it (and hiding Resume) when we don't actually know the answer.
            // Log only the message — the raw error may carry auth headers.
            console.error("[MARKETPLACE-CONTEXT:CLEAR-IF-CVO-EXISTS] CVO existence check failed; leaving context intact", e?.message);
        });
    }

    return next(action);
};
